import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FiCalendar, FiMapPin, FiPackage } from 'react-icons/fi'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import AsyncSelect from 'react-select/async'
import api, { fetcher } from '../../utils/api'
import Spinner from '../../components/Spinner'
import { useAppCountries } from '../../utils/appCountries'
import { canModifyActivity } from '../../utils/activityUtils'
import { useLocationCascade } from '../../hooks/useLocations'

const ActivityForm = ({ activity, onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false)
  const { countries } = useAppCountries()
  const [selectedFarmers, setSelectedFarmers] = useState([])

  const isReadOnly = activity && !canModifyActivity(activity)

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      country: '',
      region: '',
      district: '',
      community: '',
      commodity: '',
      activity_type: 'other',
    },
  })

  const { data: farmersData, isLoading: farmersLoading } = useSWR('farmers/', fetcher)
  const { data: commodities, isLoading: commoditiesLoading } = useSWR('commodities/', fetcher)
  const { data: activityTypesData, isLoading: activityTypesLoading } = useSWR('activities/activity_types/', fetcher)

  const watchedRegion = watch('region')
  const watchedDistrict = watch('district')
  const {
    regionNames,
    districts,
    communities,
    isLoading: locationsLoading,
  } = useLocationCascade(watchedRegion, watchedDistrict)

  const defaultFarmerOptions = farmersData?.results?.map((farmer) => ({
    value: farmer.id,
    label: `${farmer.firstname} ${farmer.lastname}`,
    email: farmer.email,
    phone: farmer.phone,
  })) || []

  const loadFarmerOptions = async (inputValue) => {
    try {
      const response = await api.get('farmers/', {
        params: { search: inputValue, page_size: 20 },
      })
      return response.data.results.map((farmer) => ({
        value: farmer.id,
        label: `${farmer.firstname} ${farmer.lastname}`,
        email: farmer.email,
        phone: farmer.phone,
      }))
    } catch (error) {
      console.error('Error loading farmers:', error)
      return []
    }
  }

  const handleFarmerChange = (selectedOptions) => {
    setSelectedFarmers(selectedOptions || [])
  }

  const activityTypes = activityTypesData?.activity_types || []

  useEffect(() => {
    if (!activity) return

    setValue('name', activity.name || '')
    setValue('description', activity.description || '')
    setValue('start_date', activity.start_date ? activity.start_date.split('T')[0] : '')
    setValue('end_date', activity.end_date ? activity.end_date.split('T')[0] : '')
    setValue('country', activity.country || '')
    setValue('region', activity.region || '')
    setValue('district', activity.district || '')
    setValue('community', activity.community || '')
    setValue('activity_type', activity.activity_type || 'other')

    const commodityName =
      activity.commodity ||
      activity.commodities?.[0]?.name ||
      ''
    setValue('commodity', commodityName)

    if (activity.farmers?.length) {
      setSelectedFarmers(
        activity.farmers.map((farmer) => ({
          value: farmer.id,
          label: `${farmer.firstname} ${farmer.lastname}`,
          email: farmer.email,
          phone: farmer.phone,
        }))
      )
    }
  }, [activity?.id, setValue])

  const onSubmitForm = async (data) => {
    if (isReadOnly) {
      toast.error('This activity has ended and can no longer be updated.')
      return
    }

    if (selectedFarmers.length === 0) {
      toast.error('Please select at least one farmer')
      return
    }

    setLoading(true)
    try {
      const activityData = {
        name: data.name,
        description: data.description,
        activity_type: data.activity_type,
        start_date: data.start_date,
        end_date: data.end_date,
        status: activity?.status || 'planned',
        country: data.country,
        region: data.region,
        district: data.district,
        community: data.community,
        farmer_ids: selectedFarmers.map((farmer) => farmer.value),
        commodity_ids: data.commodity
          ? [commodities?.find((c) => c.name?.toLowerCase() === data.commodity?.toLowerCase())?.id].filter(Boolean)
          : [],
      }

      if (activity) {
        await api.patch(`activities/${activity.id}/`, activityData)
        toast.success('Activity updated successfully')
      } else {
        await api.post('activities/', activityData)
        toast.success('Activity created successfully')
      }
      onSubmit()
    } catch (error) {
      toast.error(activity ? 'Failed to update activity' : 'Failed to create activity')
      console.error('Form submission error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-3xl relative max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark">
          <h2 className="text-xl font-bold dark:text-white">
            {activity ? 'Edit Activity' : 'Create New Activity'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        {isReadOnly && (
          <div className="mx-4 mt-4 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
            This activity has ended. Updates and farmer changes are no longer allowed.
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmitForm)} className="flex-1 overflow-y-auto p-4 space-y-6">
          <fieldset disabled={isReadOnly} className="space-y-6 disabled:opacity-80">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Activity Type<span className="text-red-500">*</span> 
              </label>
              <select
                {...register('activity_type', { required: 'Activity type is required' })}
                disabled={activityTypesLoading || isReadOnly}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white capitalize disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{activityTypesLoading ? 'Loading activity types...' : 'Select Activity Type'}</option>
                {activityTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.display}
                  </option>
                ))}
              </select>
              {errors.activity_type && (
                <p className="mt-1 text-sm text-red-600">{errors.activity_type.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Activity Name<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name', { required: 'Activity name is required' })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                placeholder="Enter activity name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description<span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                placeholder="Enter activity description"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FiCalendar className="inline mr-1" />
                  Start Date<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  {...register('start_date', { required: 'Start date is required' })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                />
                {errors.start_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FiCalendar className="inline mr-1" />
                  End Date<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  {...register('end_date', { required: 'End date is required' })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                />
                {errors.end_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country<span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('country', { required: 'Country is required' })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                  >
                    <option value="">Select Country</option>
                    {countries?.map((country) => (
                      <option key={country.id} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Region<span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('region', {
                      required: 'Region is required',
                      onChange: () => {
                        setValue('district', '')
                        setValue('community', '')
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white"
                  >
                    <option value="">Select Region</option>
                    {regionNames.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                  {errors.region && (
                    <p className="mt-1 text-sm text-red-600">{errors.region.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    District<span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('district', {
                      required: 'District is required',
                      onChange: () => {
                        setValue('community', '')
                      },
                    })}
                    disabled={locationsLoading || !watchedRegion}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">{locationsLoading ? 'Loading districts...' : 'Select District'}</option>
                    {districts.map((district) => (
                      <option key={district?.name} value={district?.name}>
                        {district?.name}
                      </option>
                    ))}
                  </select>
                  {errors.district && (
                    <p className="mt-1 text-sm text-red-600">{errors.district.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Community<span className="text-red-500">*</span>
                </label>
                <select
                  {...register('community', { required: 'Community is required' })}
                  disabled={locationsLoading || !watchedDistrict}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{locationsLoading ? 'Loading communities...' : 'Select Community'}</option>
                  {communities.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                {errors.community && (
                  <p className="mt-1 text-sm text-red-600">{errors.community.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Commodity<span className="text-red-500">*</span>
              </label>
              <select
                {...register('commodity', { required: 'Commodity is required' })}
                disabled={commoditiesLoading}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yellowcolor focus:border-yellowcolor dark:bg-dark dark:border-border-dark dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{commoditiesLoading ? 'Loading commodities...' : 'Select Commodity'}</option>
                {commodities?.map((commodity) => (
                  <option key={commodity.id} value={commodity.name}>
                    {commodity.name}
                  </option>
                ))}
              </select>
              {errors.commodity && (
                <p className="mt-1 text-sm text-red-600">{errors.commodity.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Farmers<span className="text-red-500">*</span>
              </label>
              {farmersLoading ? (
                <div className="flex justify-center items-center h-10">
                  <Spinner size="sm" />
                </div>
              ) : (
                <AsyncSelect
                  isMulti
                  isDisabled={isReadOnly}
                  loadOptions={loadFarmerOptions}
                  defaultOptions={defaultFarmerOptions}
                  value={selectedFarmers}
                  onChange={handleFarmerChange}
                  placeholder="Search and select farmers..."
                  className="text-sm"
                  classNamePrefix="select"
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? '#fbbf24' : '#e5e7eb',
                      boxShadow: state.isFocused ? '0 0 0 1px #fbbf24' : 'none',
                      backgroundColor: 'white',
                      minHeight: '42px',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#fbbf24' : state.isFocused ? '#fef3c7' : 'white',
                      color: state.isSelected ? 'white' : 'black',
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#fbbf24',
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: 'white',
                    }),
                  }}
                />
              )}
              {selectedFarmers.length === 0 && (
                <p className="mt-1 text-sm text-red-600">Please select at least one farmer</p>
              )}
            </div>
          </fieldset>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-dark dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-yellowcolor text-black hover:bg-yellowcolor/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {activity ? (loading ? 'Updating...' : 'Update Activity') : (loading ? 'Please wait...' : 'Create Activity')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default ActivityForm
